// Freigaben (Vier-Augen-Prinzip): offene und erledigte Anfragen, Prüfen mit Vorschau (M20) und Vergleich,
// Freigeben / Ablehnen mit Begründung / Zurückziehen, Einstellungen des Mandanten.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import {
  targetPath, useApproval, useApprovals, useApprovalSettings, useApprovalState, useRefreshApprovals, type ApprovalComparison, type ApprovalDetail,
  type ApprovalRequest, type ApprovalSettings, type ComparisonEntry, type EntryValues,
} from '../api/approvals';
import { can, useMe, useMedia } from '../api/queries';
import { Icon } from '../components/Icon';
import { LayoutPreview, useMediaIndex } from '../components/Preview';
import { useToast } from '../components/toast';
import { Dialog, Empty, Field, formatDateTime, formatDuration, PageHead, Spinner } from '../components/ui';
import { localeOf } from '../i18n';
import { WEEKDAY_BITS } from '../lib';
import { previewPath } from '../preview/open';

type Tab = 'pending' | 'done' | 'settings';

const STATUS_PILL: Record<ApprovalRequest['status'], string> = { pending: 'warn', approved: 'ok', rejected: 'rejected', withdrawn: 'neutral' };
const TYPE_ICON = { playlist: 'list', layout: 'layout', schedule: 'calendar' } as const;

export function ApprovalsPage() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: state } = useApprovalState();
  const [params, setParams] = useSearchParams();
  const canSettings = can(me, 'tenant.settings.manage');
  const p = params.get('tab');
  const tab: Tab = p === 'settings' && canSettings ? 'settings' : p === 'done' ? 'done' : 'pending';
  const setTab = (k: Tab) => setParams(k === 'pending' ? {} : { tab: k }, { replace: true });
  return (
    <>
      <PageHead title={t('approval.title')} sub={t('approval.sub')} />
      <div className="tabs" role="tablist">
        {(['pending', 'done', ...(canSettings ? ['settings'] : [])] as Tab[]).map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
            {t(`approval.tab.${k}`)}{k === 'pending' && state?.toDecide ? <span className="nav-count" aria-hidden>{state.toDecide}</span> : null}
          </button>
        ))}
      </div>
      {state && !state.enabled && tab !== 'settings' && (
        <div className="hint" style={{ marginBottom: 14 }} role="note">
          {t('approval.disabledHint')}{' '}
          {canSettings && <button className="btn sm" onClick={() => setTab('settings')}>{t('approval.tab.settings')}</button>}
        </div>
      )}
      {tab === 'settings' ? <SettingsTab /> : <RequestList status={tab} />}
    </>
  );
}

function RequestList({ status }: { status: 'pending' | 'done' }) {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const navigate = useNavigate();
  const { data, isLoading } = useApprovals(status);
  if (isLoading) return <Spinner />;
  if (!data?.length) {
    return <div className="card"><Empty icon="check" title={t(status === 'pending' ? 'approval.empty.pending' : 'approval.empty.done')}
      text={status === 'pending' ? t('approval.empty.pendingText') : undefined} /></div>;
  }
  return (
    <div className="card scroll" style={{ padding: 10 }}>
      <table className="list" data-testid={`approvals-${status}`}>
        <thead><tr>
          <th>{t('approval.col.object')}</th><th>{t('approval.col.submittedBy')}</th><th>{t('approval.col.submittedAt')}</th>
          <th>{t('approval.col.status')}</th>{status === 'done' && <th>{t('approval.col.decision')}</th>}<th />
        </tr></thead>
        <tbody>
          {data.map((r) => (
            <tr key={r.id} className="click" onClick={() => navigate(`/approvals/${r.id}`)}>
              <td>
                <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
                  <Icon name={TYPE_ICON[r.targetType]} />
                  <div><b>{r.targetName}</b><div className="muted small">{t(`approval.type.${r.targetType}`)}{r.note ? ` · ${r.note}` : ''}</div></div>
                </div>
              </td>
              <td>{r.submittedBy.label}</td>
              <td className="muted" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.submittedAt, locale)}</td>
              <td style={{ whiteSpace: 'nowrap' }}><span className={`pill ${STATUS_PILL[r.status]}`}>{t(`approval.status.${r.status}`)}</span></td>
              {status === 'done' && <td className="muted small">{r.decidedBy?.label ?? ''}{r.decisionComment ? ` · ${r.decisionComment}` : ''}</td>}
              <td style={{ textAlign: 'right' }}>
                <Link className={`btn sm${r.canDecide ? ' primary' : ''}`} to={`/approvals/${r.id}`} onClick={(e) => e.stopPropagation()}>
                  {r.canDecide ? t('approval.review') : t('approval.pending.show')}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SettingsTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshApprovals();
  const { data, isLoading } = useApprovalSettings();
  const [form, setForm] = useState<Omit<ApprovalSettings, 'rowVersion' | 'adminCount'> | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data && !form) setForm({ enabled: data.enabled, allowSelfApproval: data.allowSelfApproval, notifyEmail: data.notifyEmail });
  }, [data, form]);
  if (isLoading || !data || !form) return <Spinner />;
  const save = async () => {
    setBusy(true);
    try {
      await api.put<ApprovalSettings>('/approvals/settings', form, { ifMatch: data.rowVersion });
      await refresh();
      toast.show(t('approval.settings.saved'));
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const check = (k: keyof typeof form, label: string, hint?: string, disabled = false) => (
    <label className="row" style={{ alignItems: 'flex-start', gap: 10, flexWrap: 'nowrap' }}>
      <input type="checkbox" checked={form[k]} disabled={disabled} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} style={{ marginTop: 3 }} />
      <span style={{ flex: 1, minWidth: 0 }}><b>{label}</b>{hint ? <div className="muted small">{hint}</div> : null}</span>
    </label>
  );
  return (
    <div className="card col" style={{ gap: 16, maxWidth: 720 }}>
      <h3 style={{ margin: 0 }}>{t('approval.settings.title')}</h3>
      {check('enabled', t('approval.settings.enabled'), t('approval.settings.enabledHint'))}
      {check('allowSelfApproval', t('approval.settings.selfApproval'), `${t('approval.settings.admins', { count: data.adminCount })} ${t('approval.settings.selfApprovalHint')}`, !form.enabled)}
      {check('notifyEmail', t('approval.settings.notify'), t('approval.settings.notifyHint'), !form.enabled)}
      <div className="row"><button className="btn primary" disabled={busy} onClick={() => void save()}>{t('common.save')}</button></div>
    </div>
  );
}

// ---------- Einzelne Anfrage ----------

export function ApprovalDetailPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: me } = useMe();
  const { data: r, isLoading, error } = useApproval(id);
  const refresh = useRefreshApprovals();
  const toast = useToast();
  const [dialog, setDialog] = useState<'approve' | 'reject' | 'withdraw' | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  if (isLoading) return <Spinner />;
  if (!r) return <Empty icon="alert" title={t('common.notFound')} text={error instanceof Error ? error.message : undefined} />;

  const act = async (kind: 'approve' | 'reject' | 'withdraw') => {
    setBusy(true);
    try {
      if (kind === 'withdraw') await api.post(`/approvals/${r.id}/withdraw`);
      else await api.post(`/approvals/${r.id}/${kind}`, comment.trim() ? { comment: comment.trim() } : {});
      toast.show(t(kind === 'approve' ? 'approval.approved' : kind === 'reject' ? 'approval.rejected' : 'approval.withdrawn'));
      setDialog(null);
      setComment('');
      await refresh();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const own = r.submittedBy.id === me?.user.id;
  const pending = r.status === 'pending';
  const hasPublished = r.targetType === 'schedule' || r.comparison.publishedRevisionNo !== null;
  const open = (params: Record<string, string>) => window.open(previewPath(r.targetType, r.targetId, params), '_blank');

  return (
    <>
      <PageHead
        title={<span className="row" style={{ gap: 10 }}><Icon name={TYPE_ICON[r.targetType]} />{r.targetName}</span>}
        sub={<>{t(`approval.type.${r.targetType}`)} · <span className={`pill ${STATUS_PILL[r.status]}`}>{t(`approval.status.${r.status}`)}</span></>}
        actions={<div className="row" style={{ flexWrap: 'wrap' }}>
          <Link className="btn ghost" to="/approvals"><Icon name="left" />{t('approval.backToList')}</Link>
          {r.targetExists && pending && <button className="btn" onClick={() => open({ approval: r.id })}><Icon name="play" />{t('approval.previewSubmitted')}</button>}
          {r.targetExists && hasPublished && <button className="btn" onClick={() => open({ source: 'published' })}><Icon name="play" />{t('approval.previewPublished')}</button>}
          {r.canWithdraw && <button className="btn ghost danger" onClick={() => setDialog('withdraw')}>{t('approval.withdraw')}</button>}
          {r.canDecide && <>
            <button className="btn danger" onClick={() => setDialog('reject')}><Icon name="x" />{t('approval.reject')}</button>
            <button className="btn primary" disabled={!r.targetExists} onClick={() => setDialog('approve')}><Icon name="check" />{t('approval.approve')}</button>
          </>}
        </div>}
      />

      <div className="card col" style={{ gap: 8, marginBottom: 16 }}>
        <div>{t('approval.submittedLine', { who: r.submittedBy.label, when: formatDateTime(r.submittedAt, locale) })}</div>
        {r.note && <div><b>{t('approval.note')}:</b> {r.note}</div>}
        {r.decidedAt && r.status !== 'pending' && (
          <div>{t(`approval.decided.${r.status}`, { who: r.decidedBy?.label ?? '', when: formatDateTime(r.decidedAt, locale) })}
            {r.decisionComment ? <> · <b>{t(r.status === 'rejected' ? 'approval.reason' : 'approval.note')}:</b> {r.decisionComment}</> : null}</div>
        )}
        {r.resultRevisionNo && <div className="muted small">{t('approval.resultRevision', { no: r.resultRevisionNo })}</div>}
        {!r.targetExists && <div className="hint warn">{t('approval.targetGone')}</div>}
        {pending && !r.canDecide && (own ? <div className="muted small">{t('approval.selfBlocked')}</div>
          : !can(me, 'content.approve') ? <div className="muted small">{t('approval.noPermission')}</div> : null)}
        {r.targetExists && <div><Link to={targetPath(r.targetType, r.targetId)}>{r.targetName} →</Link></div>}
      </div>

      <Comparison r={r} />

      {dialog === 'approve' && (
        <Dialog title={t('approval.approveTitle', { name: r.targetName })} onClose={() => setDialog(null)} actions={<>
          <button className="btn ghost" onClick={() => setDialog(null)}>{t('common.cancel')}</button>
          <button className="btn primary" disabled={busy} onClick={() => void act('approve')}><Icon name="check" />{t('approval.approve')}</button>
        </>}>
          <div className="muted">{t('approval.approveText')}</div>
          <Field label={t('approval.commentOptional')}><textarea value={comment} maxLength={2000} onChange={(e) => setComment(e.target.value)} /></Field>
        </Dialog>
      )}
      {dialog === 'reject' && (
        <Dialog title={t('approval.rejectTitle', { name: r.targetName })} onClose={() => setDialog(null)} actions={<>
          <button className="btn ghost" onClick={() => setDialog(null)}>{t('common.cancel')}</button>
          <button className="btn danger solid" disabled={busy || comment.trim().length < 3} onClick={() => void act('reject')}>{t('approval.reject')}</button>
        </>}>
          <div className="muted">{t('approval.rejectText')}</div>
          <Field label={t('approval.reason')}><textarea value={comment} maxLength={2000} onChange={(e) => setComment(e.target.value)} placeholder={t('approval.reasonPh')} /></Field>
        </Dialog>
      )}
      {dialog === 'withdraw' && (
        <Dialog title={t('approval.withdrawTitle')} onClose={() => setDialog(null)} actions={<>
          <button className="btn ghost" onClick={() => setDialog(null)}>{t('common.cancel')}</button>
          <button className="btn danger solid" disabled={busy} onClick={() => void act('withdraw')}>{t('approval.withdraw')}</button>
        </>}>
          <div className="muted">{t('approval.withdrawText')}</div>
        </Dialog>
      )}
    </>
  );
}

const CHANGE_PILL: Record<ComparisonEntry['change'], string> = { added: 'ok', removed: 'rejected', changed: 'warn', unchanged: 'neutral' };

function Comparison({ r }: { r: ApprovalDetail }) {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const c: ApprovalComparison = r.comparison;
  const fields = c.fields.filter((f) => f.changed && f.key !== 'definition');
  const changed = c.entries.filter((e) => e.change !== 'unchanged');
  const fieldValue = (key: string, v: string | null) => {
    if (v === null) return '–';
    if (key === 'defaultDurationMs') return formatDuration(Number(v));
    if (key === 'transition') return t(v === 'fade' ? 'approval.compare.transitionValue.fade' : 'approval.compare.transitionValue.none');
    if (key === 'orientation') return v === 'portrait' ? '9:16' : '16:9';
    return v;
  };
  const sameDesign = c.fields.find((f) => f.key === 'definition')?.changed === false;
  return (
    <div className="card" style={{ marginBottom: 16 }} data-testid="approval-compare">
      <div className="row" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }} className="grow">{t('approval.compare.title')}</h3>
        <span className="muted small">{r.targetType === 'schedule' ? t('approval.compare.liveSchedule')
          : c.publishedRevisionNo ? t('approval.compare.publishedRev', { no: c.publishedRevisionNo }) : t('approval.compare.neverPublished')}</span>
      </div>
      {fields.length > 0 && (
        <dl className="props" style={{ marginBottom: 12 }}>
          {fields.map((f) => <FieldRow key={f.key} label={t(`approval.compare.field.${f.key}`)} before={fieldValue(f.key, f.before)} after={fieldValue(f.key, f.after)} />)}
        </dl>
      )}
      {c.layout && <LayoutCompare layout={c.layout} same={sameDesign} />}
      {c.entries.length > 0 && (
        <ol className="list-plain" style={{ display: 'grid', gap: 6, paddingLeft: 0 }}>
          {c.entries.map((e, i) => (
            <li key={i} className="row" style={{ gap: 10, alignItems: 'flex-start', opacity: e.change === 'unchanged' ? 0.7 : 1 }}>
              <span className={`pill plain ${CHANGE_PILL[e.change]}`} style={{ minWidth: 92, justifyContent: 'center' }}>{t(`approval.compare.change.${e.change}`)}</span>
              <span className="grow">
                <b>{e.label}</b>{' '}
                <span className="muted small">{describeEntry(e, t, locale)}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
      {!fields.length && !changed.length && !(c.layout && !sameDesign) && <div className="muted">{t('approval.compare.noChanges')}</div>}
    </div>
  );
}

function FieldRow({ label, before, after }: { label: string; before: string; after: string }) {
  return <><dt>{label}</dt><dd><s className="muted">{before}</s> → <b>{after}</b></dd></>;
}

function LayoutCompare({ layout, same }: { layout: NonNullable<ApprovalComparison['layout']>; same: boolean }) {
  const { t } = useTranslation();
  useMedia();
  const media = useMediaIndex();
  const ratio = layout.orientation === 'portrait' ? '9 / 16' : '16 / 9';
  const box = (def: unknown, label: string) => (
    <figure style={{ margin: 0, flex: '1 1 260px', maxWidth: layout.orientation === 'portrait' ? 220 : 420 }}>
      <figcaption className="muted small" style={{ marginBottom: 4 }}>{label}</figcaption>
      <div style={{ position: 'relative', aspectRatio: ratio, borderRadius: 8, overflow: 'hidden', background: '#000' }}>
        {def ? <LayoutPreview definition={def} media={media} /> : <div className="muted small" style={{ padding: 12 }}>{t('approval.compare.neverPublished')}</div>}
      </div>
    </figure>
  );
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="row" style={{ gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {box(layout.published, t('approval.compare.publishedDesign'))}
        {box(layout.submitted, t('approval.compare.submittedDesign'))}
      </div>
      <div className="muted small" style={{ marginTop: 6 }}>{same ? t('approval.compare.definitionSame') : t('approval.compare.definitionChanged')}</div>
    </div>
  );
}

type T = (k: string, o?: Record<string, unknown>) => string;

function ruleText(v: EntryValues, t: T): string {
  const short = t('schedules.weekdaysShort', { returnObjects: true }) as unknown as string[];
  const mask = Number(v.weekdays ?? 0);
  const days = v.ruleType === 'weekly'
    ? WEEKDAY_BITS.flatMap((b, i) => ((mask & b) !== 0 ? [short[i] ?? ''] : [])).join(', ')
    : v.dateUntil && v.dateUntil !== v.dateFrom ? `${v.dateFrom} – ${v.dateUntil}` : String(v.dateFrom ?? '');
  const time = v.timeFrom || v.timeUntil ? `${v.timeFrom ?? '00:00'}–${v.timeUntil ?? '24:00'}` : t('preview.allDay');
  return `${days} ${time} · ${t('approval.compare.key.priority')} ${v.priority}${v.enabled === false ? ` · ${t('approval.compare.key.enabled')}: –` : ''}`;
}

function describeEntry(e: ComparisonEntry, t: T, locale: string): string {
  const v = e.after ?? e.before ?? {};
  const base = e.kind === 'rule' ? ruleText(v, t) : v.durationMs ? formatDuration(Number(v.durationMs)) : '';
  if (e.change !== 'changed') return base;
  if (e.kind === 'rule') return `${base} · ${t('approval.compare.before')}: ${ruleText(e.before ?? {}, t)}`;
  const parts = e.changedKeys.map((k) => {
    const b = e.before?.[k] ?? null;
    const a = e.after?.[k] ?? null;
    if (k === 'durationMs') return `${t('approval.compare.key.durationMs')}: ${b ? formatDuration(Number(b)) : '–'} → ${a ? formatDuration(Number(a)) : '–'}`;
    if (k === 'validFrom' || k === 'validUntil') return `${t(`approval.compare.key.${k}`)}: ${formatDateTime(b as string | null, locale)} → ${formatDateTime(a as string | null, locale)}`;
    if (k === 'file' || k === 'content' || k === 'layoutRevisionNo') return t(`approval.compare.key.${k}`);
    return `${t(`approval.compare.key.${k}`, { defaultValue: k })}: ${String(b ?? '–')} → ${String(a ?? '–')}`;
  });
  return parts.join(' · ');
}
